/**
 * Runtime configuration loaded from `/config.json` at SPA boot.
 *
 * The file is generated by the WebStack at deploy time
 * (`infrastructure/lib/web-stack.ts` → `s3deploy.Source.jsonData`),
 * so deploying the backend stacks + the web stack is one `cdk deploy`
 * with no manual env-var shuffling. CFN tokens in `Source.jsonData`
 * resolve to the real CFN attributes at deploy time.
 *
 * Dev mode (Vite, Storybook, vitest) short-circuits to hardcoded
 * defaults instead of fetching — the Floci dev loop doesn't deploy
 * the web stack, so there's no `/config.json` to fetch.
 *
 * Lifecycle:
 *   1. `main.tsx` awaits `loadRuntimeConfig()` before rendering.
 *   2. The result is stashed in a module-level slot.
 *   3. Components read it synchronously via `getRuntimeConfig()`.
 *
 * Anyone that imports `getRuntimeConfig()` before `main.tsx` finishes
 * (tests, Storybook, SSR) gets the dev defaults — never a throw.
 */

export interface RuntimeConfig {
  /** Cognito User Pool ID — empty string when Cognito isn't configured (Floci). */
  cognitoUserPoolId: string;
  /** Cognito User Pool Client ID — empty string when not configured. */
  cognitoUserPoolClientId: string;
  /** AWS region the user pool lives in (e.g. `eu-west-1`). */
  cognitoRegion: string;
  /**
   * Absolute backend API base URL. Empty string means "use
   * `window.location.origin + '/api'`" — i.e. the Vite dev flow that
   * proxies same-origin to the CORS proxy.
   */
  apiUrl: string;
  /** User-facing brand name. Mirrors the same `APP_NAME` env var the
   * Cognito CustomMessage Lambda reads, so the SPA + emails stay
   * aligned without a second source of truth. */
  appName: string;
}

/**
 * Dev defaults. The backend `requireCognito` recognises the
 * `x-user-sub` / `x-user-email` headers when these are empty
 * (Floci-only), so the host shell stays usable without a real
 * user pool.
 *
 * `VITE_BACKEND_URL` is honoured as an escape hatch for anyone
 * pointing the SPA at a backend on a different port (e.g. the
 * standalone `pnpm --filter @massimo-quiz/backend dev` server on
 * :4488 instead of going through the cors-proxy → Floci path).
 * Default empty → SPA computes `${window.location.origin}/api`,
 * which Vite proxies to localhost:4577.
 */
const DEV_DEFAULTS: RuntimeConfig = {
  cognitoUserPoolId: '',
  cognitoUserPoolClientId: '',
  cognitoRegion: 'eu-west-1',
  apiUrl: (import.meta.env.VITE_BACKEND_URL as string | undefined) ?? '',
  appName: (import.meta.env.VITE_APP_NAME as string | undefined) ?? 'Family Quiz',
};

let currentConfig: RuntimeConfig = DEV_DEFAULTS;
let loadPromise: Promise<RuntimeConfig> | null = null;

/**
 * Load `/config.json` once and cache it. Dev mode resolves
 * immediately with `DEV_DEFAULTS` — no fetch, no network round-trip.
 *
 * In prod a fetch failure (404, parse error) is non-fatal: we log
 * and keep the dev defaults. That's the same fallback Floci uses,
 * so a broken deploy degrades gracefully to "dev mode" rather than
 * a blank screen.
 */
export async function loadRuntimeConfig(): Promise<RuntimeConfig> {
  if (loadPromise) return loadPromise;

  // `import.meta.env.DEV` is replaced at Vite build time. Production
  // bundles always evaluate this to `false` after dead-code elimination.
  if (import.meta.env.DEV) {
    loadPromise = Promise.resolve(DEV_DEFAULTS);
    return loadPromise;
  }

  loadPromise = (async () => {
    try {
      // Same-origin fetch — `/config.json` is served from the SPA's
      // own CloudFront distribution. No CORS, no auth, no cache
      // concerns (the WebStack invalidates `/config.json` on every
      // deploy so a refresh picks up new identifiers).
      const res = await fetch('/config.json', { cache: 'no-cache' });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const parsed = (await res.json()) as Partial<RuntimeConfig>;
      currentConfig = {
        ...DEV_DEFAULTS,
        ...parsed,
      };
    } catch (err) {
      // Don't blow up the page. The defaults keep the SPA bootable
      // (it'll just render the dev/anonymous path), and the error
      // shows up in the console for operators to find.
      // eslint-disable-next-line no-console
      console.error('[runtime-config] failed to load /config.json', err);
    }
    return currentConfig;
  })();
  return loadPromise;
}

/**
 * Synchronous accessor. Always returns *something* — `DEV_DEFAULTS`
 * if `loadRuntimeConfig()` hasn't resolved yet — so component
 * effects + hooks can read without awaiting.
 */
export function getRuntimeConfig(): RuntimeConfig {
  return currentConfig;
}
